/**
 * Single source of truth for routes, navigation and per-page metadata.
 * Descriptions are drawn from each page's own opening copy so nothing here
 * asserts anything the site does not already say.
 */

export const SITE_NAME = 'GodEngine'
export const SITE_TAGLINE = 'Business Intelligence Simulator'
export const SITE_TITLE_SUFFIX = `${SITE_TAGLINE} · ${SITE_NAME}`

/** Absolute origin, used for canonical + Open Graph URLs. No trailing slash. */
export const SITE_URL = (
  (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://bis.godengine.ai'
).replace(/\/+$/, '')

export const OG_IMAGE_PATH = '/brand/og-cover.png'

export type RouteKey =
  | 'home'
  | 'method'
  | 'sections'
  | 'projection'
  | 'provenance'
  | 'security'
  | 'company'
  | 'faq'
  | 'contact'

export interface RouteMeta {
  key: RouteKey
  path: string
  /** Short label used in the header nav. */
  navLabel: string
  /** Longer label used in the footer, where there is room for it. */
  footerLabel: string
  title: string
  description: string
}

export const ROUTES: Record<RouteKey, RouteMeta> = {
  home: {
    key: 'home',
    path: '/',
    navLabel: 'Home',
    footerLabel: 'Home',
    title: `Business Intelligence Simulator`,
    description:
      'A company hands over its uncurated raw business data. We arrange it, then show what is recorded in its own documents and what is projected as a range across seven sections.',
  },
  method: {
    key: 'method',
    path: '/method',
    navLabel: 'How it works',
    footerLabel: 'How it works',
    title: 'How it works — the eight-stage data plane',
    description:
      'Between your export and a panel there are eight stages, and none of them throw anything away. Land, parse, discover, profile, calendar, normalize, reconcile, assemble.',
  },
  sections: {
    key: 'sections',
    path: '/sections',
    navLabel: 'Seven sections',
    footerLabel: 'The seven sections',
    title: 'The seven sections',
    description:
      'Section depth follows data availability, not ambition. Every section, what it computes on an accounting export alone, and what each other panel is waiting for.',
  },
  projection: {
    key: 'projection',
    path: '/projection',
    navLabel: 'Projection',
    footerLabel: 'Recorded and projected',
    title: 'Projection — the next three quarters, as a range',
    description:
      'A projection is p10, p50 and p90 per period, never a point estimate. Every projection names its tier, decoder, training points, holdout and fit.',
  },
  provenance: {
    key: 'provenance',
    path: '/provenance',
    navLabel: 'Provenance',
    footerLabel: 'Provenance and trace',
    title: 'Provenance — a trace is a record of the operation',
    description:
      'A citation is a claim about a source. A trace is a record of the operation: five ordered stages, each with its own rows, each keyed to the source the figure was read from.',
  },
  security: {
    key: 'security',
    path: '/security',
    navLabel: 'Data & security',
    footerLabel: 'Data & security',
    title: 'Data & security',
    description:
      'You are handing over your general ledger. What is enforced in code, what is a stated posture, and what we have not yet proven — listed separately.',
  },
  company: {
    key: 'company',
    path: '/company',
    navLabel: 'Company',
    footerLabel: 'About GodEngine',
    title: 'Company',
    description:
      'GodEngine builds decision software. The Business Intelligence Simulator is its newest surface, and every claim on this site carries a tag.',
  },
  faq: {
    key: 'faq',
    path: '/faq',
    navLabel: 'FAQ',
    footerLabel: 'FAQ',
    title: 'Frequently asked questions',
    description:
      'Questions answered at the level they were asked. Where an answer is "not yet", it says not yet.',
  },
  contact: {
    key: 'contact',
    path: '/contact',
    navLabel: 'Contact',
    footerLabel: 'Contact',
    title: 'Contact',
    description:
      'Tell us what your exports look like. The most useful first message describes the files you already have — what produced them and what shape they are in.',
  },
}

export const ROUTE_LIST: RouteMeta[] = Object.values(ROUTES)

/** Header navigation, in the order the design shows it. */
export const PRIMARY_NAV: RouteMeta[] = [
  ROUTES.method,
  ROUTES.sections,
  ROUTES.projection,
  ROUTES.provenance,
  ROUTES.security,
  ROUTES.company,
  ROUTES.faq,
]

export const FOOTER_NAV: { heading: string; items: RouteMeta[] }[] = [
  {
    heading: 'Product',
    items: [ROUTES.method, ROUTES.sections, ROUTES.projection, ROUTES.provenance],
  },
  {
    heading: 'Company',
    items: [ROUTES.company, ROUTES.security, ROUTES.faq, ROUTES.contact],
  },
]

/** The contact route carries an intent so the "design partner" CTA lands pre-selected. */
export const PARTNER_CONTACT_PATH = '/contact?intent=partner'

/**
 * The previous build used hash routes (`#/method`). Real URLs are better for
 * SEO and sharing, so those old links are redirected rather than broken.
 */
export const LEGACY_HASH_ROUTES: Record<string, string> = {
  '#/home': '/',
  '#/method': '/method',
  '#/sections': '/sections',
  '#/projection': '/projection',
  '#/provenance': '/provenance',
  '#/security': '/security',
  '#/company': '/company',
  '#/faq': '/faq',
  '#/contact': '/contact',
  '#/contact--partner': PARTNER_CONTACT_PATH,
}

/**
 * The way into the product.
 *
 * A PATH, not an absolute URL: the marketing site and the application are served from the same
 * origin, so hardcoding a domain here would break every preview, staging host and local run.
 * It is not a react-router route -- /login belongs to the Next.js application -- so every link
 * to it must be a hard navigation (`<ButtonLink hard>`), or the router resolves it against its
 * own table and renders the 404 page.
 */
export const TERMINAL_PATH = '/login'
